import {
  PenumbraDecryptionInfo,
  penumbra as penumbraLib,
} from '@transcend-io/penumbra';
import { FileTypeEnum, IDdoc, MigrationStatus, PublishStatus } from '@/types';
import {
  generateRandomAESKey,
  generateRandomNonce,
  getNonceAppendedCipherText,
} from './crypto-utils';
import tweetnacl from 'tweetnacl';
import { CanceledError } from 'axios';
import {
  ARCH_VERSION,
  FILE_SOURCE_MAP,
  LATEST_DDOC_VERSION,
  VERSION_AFTER_YJS_UPDATE,
} from './constants';

import { fromUint8Array, toUint8Array } from 'js-base64';
import { db, UserCache } from '@/db/db';
import {
  getCachedDoc,
  getExistingDocOnIndexeddb,
  modifyUserCacheData,
} from '@/db/queries';
import { isNullish } from './gun-utils';
import { addOrEditFileTransaction } from './portal-utils';
import { uploadContentAndMetadataFile } from './upload-utils';
import { captureException } from '@sentry/nextjs';
import { withRetry } from './helper';
import { AgentInstance } from './identity-utils/smart-account-agent';
import { KeyStoreInstance } from './identity-utils/key-store';
import { WorkerInstance } from './worker-utils/worker-instance';
import {
  FAILED_USER_OP_STATUSES,
  FILE_TRX_TIMEOUT,
  getUserOpStatus,
  PENDING_TXN_TIMEOUT,
  waitForUserOpReceipt,
} from './account-utils';
import {
  CONTRACT_ABI_MAP,
  ContractType,
  PortalContractEvent,
} from './contract-utils';
import { Hex, parseEventLogs } from 'viem';
import { generateDocWsKey } from './socket-client-utils';

const FAILED_IPFS_FETCH_ERROR =
  'Oops failed to load the dDoc from IPFS storage. Please refresh.';

const getPenumbra = () => window?.penumbra || penumbraLib;

const setPenumbraWorkerLocation = async () => {
  const penumbra = getPenumbra();

  if (penumbra) {
    //@ts-ignore
    if (window.penumbraInitialised) return;
    await penumbra.setWorkerLocation('/penumbra/worker.penumbra.js');
    //@ts-ignore
    window.penumbraInitialised = true;
  }
};

export const encryptFile = async (file: File) => {
  await setPenumbraWorkerLocation();
  const penumbra = getPenumbra();
  const stream: ArrayBuffer = await file.arrayBuffer();

  const [encrypted] = await penumbra.encrypt(null, {
    stream: new Response(new Uint8Array(stream))
      .body as ReadableStream<Uint8Array>,
    size: file.size,
  });

  const decryptionInfo: PenumbraDecryptionInfo =
    await penumbra.getDecryptionInfo(encrypted);

  const fileBlob = await penumbra.getBlob(encrypted);
  const encryptedFile = new File([fileBlob], file.name);

  return {
    fileKey: {
      key: fromUint8Array(decryptionInfo.key as Uint8Array),
      iv: fromUint8Array(decryptionInfo.iv as Uint8Array),
      authTag: fromUint8Array(decryptionInfo.authTag as Uint8Array),
    },
    encryptedFile,
  };
};

export const jsonToFile = (data: any, fileName: string) => {
  const blob = new Blob([JSON.stringify(data)], {
    type: 'application/json',
  });

  const file = new File([blob], fileName, {
    type: 'application/json',
  });

  return file;
};

export const jsonToBytes = (json: Record<string, any>) =>
  new TextEncoder().encode(JSON.stringify(json));

export const createLinkLock = async (
  encryptionKey: Uint8Array,
  dataKeyMaterial: any
) => {
  const fileKeyNonce = generateRandomNonce();
  const encryptedFileKey = tweetnacl.secretbox(
    jsonToBytes(dataKeyMaterial.fileKey),
    fileKeyNonce,
    encryptionKey
  );

  const lockedFileKey = getNonceAppendedCipherText(
    fileKeyNonce,
    encryptedFileKey
  );
  const chatKeyNonce = generateRandomNonce();
  const encryptedChatKey = tweetnacl.secretbox(
    jsonToBytes(dataKeyMaterial.fileChatKeyPair),
    chatKeyNonce,
    encryptionKey
  );
  const lockedChatKey = getNonceAppendedCipherText(
    chatKeyNonce,
    encryptedChatKey
  );

  const linkKeyNonce = generateRandomNonce();
  const encryptedLinkKey = tweetnacl.secretbox(
    toUint8Array(dataKeyMaterial.linkKey),
    linkKeyNonce,
    encryptionKey
  );
  const lockedLinkKey = getNonceAppendedCipherText(
    linkKeyNonce,
    encryptedLinkKey
  );
  const rtcKeyNonce = generateRandomNonce();
  const encryptedRtcKey = tweetnacl.secretbox(
    jsonToBytes(dataKeyMaterial.rtcKey),
    rtcKeyNonce,
    encryptionKey
  );
  const lockedRtcKey = getNonceAppendedCipherText(rtcKeyNonce, encryptedRtcKey);

  return {
    lockedFileKey,
    lockedChatKey,
    lockedLinkKey,
    lockedRtcKey,
  };
};

const toAesKey = async (key: Uint8Array) => {
  return await window.crypto.subtle.importKey(
    'raw',
    key,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
};

export const encryptTitleWithFileKey = async (
  title: string,
  fileKey: {
    key: string;
    iv: string;
    authTag: string;
  }
) => {
  const key = await toAesKey(toUint8Array(fileKey.key));

  const iv = toUint8Array(fileKey.iv);

  const encryptedTitle = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
      tagLength: 128,
    },
    key,
    new TextEncoder().encode(title)
  );

  return new Uint8Array(encryptedTitle);
};

export const decryptTitleWithFileKey = async (
  encryptedTitle: string,
  fileKey: {
    key: string;
    iv: string;
    authTag: string;
  }
) => {
  const key = await toAesKey(toUint8Array(fileKey.key));
  const iv = toUint8Array(fileKey.iv);

  const decryptedTitle = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
      tagLength: 128,
    },
    key,
    toUint8Array(encryptedTitle)
  );

  return new TextDecoder().decode(decryptedTitle);
};

export const generateFileMetadata = async (
  fileSize: number,
  fileKey: {
    key: string;
    iv: string;
    authTag: string;
  },
  docItem: Partial<IDdoc>,
  secretKey: Uint8Array,
  nonce: Uint8Array,
  linkKey: string,
  version: string,
  rtcKey: string
) => {
  const title = docItem.title || 'Untitled';
  const dataKeyMaterial = {
    fileKey,
    fileChatKeyPair: docItem.commentKey,
    linkKey,
    rtcKey,
  };

  const portalLock = {
    lockedFileKey: await KeyStoreInstance.encryptData(
      dataKeyMaterial.fileKey,
      'portal'
    ),
    lockedChatKey: await KeyStoreInstance.encryptData(
      dataKeyMaterial.fileChatKeyPair,
      'portal'
    ),
    lockedLinkKey: await KeyStoreInstance.encryptData(
      dataKeyMaterial.linkKey,
      'portal'
    ),
    lockedRtcKey: await KeyStoreInstance.encryptData(
      dataKeyMaterial.rtcKey,
      'portal'
    ),
  };

  const ownerLock = {
    lockedFileKey: await KeyStoreInstance.encryptData(
      dataKeyMaterial.fileKey,
      'owner'
    ),
    lockedChatKey: await KeyStoreInstance.encryptData(
      dataKeyMaterial.fileChatKeyPair,
      'owner'
    ),
    lockedLinkKey: await KeyStoreInstance.encryptData(
      dataKeyMaterial.linkKey,
      'owner'
    ),
    lockedRtcKey: await KeyStoreInstance.encryptData(
      dataKeyMaterial.rtcKey,
      'owner'
    ),
  };

  const linkLock = await createLinkLock(secretKey, dataKeyMaterial);

  const encryptedTitle = fromUint8Array(
    await encryptTitleWithFileKey(title, fileKey),
    true
  );

  return {
    title: encryptedTitle,
    size: fileSize,
    mimeType: 'application/json',
    portalLock,
    ownerLock,
    linkLock,
    ddocId: docItem.ddocId,
    nonce: fromUint8Array(nonce),
    owner: AgentInstance.getAgentAddress(),
    archVersion: ARCH_VERSION,
    version,
  };
};

export const parallelFetch = async (urls: string[]) => {
  const AbortControllerMap = new Map<string, AbortController>();

  const requests = [];
  for (const url of urls) {
    const controller = new AbortController();
    AbortControllerMap.set(url, controller);
    requests.push(
      fetch(url, { signal: controller.signal })
        .then((response) => {
          if (response.status === 200) {
            Array.from(AbortControllerMap.entries()).forEach(([key, value]) => {
              if (key !== url) {
                value.abort(new CanceledError());
              }
            });
            return response;
          }
          throw new Error('Failed with status code ' + response?.status);
        })
        .catch((error) => {
          if (error?.code !== 'ERR_CANCELED') {
            console.error(`Failed to fetch from ${url}`, error);
            throw error;
          }
        })
    );
  }

  const responses = await Promise.any(requests);
  return responses;
};

export const fetchFromIPFS = async (hash: string) => {
  try {
    // if (!isIPFS.multihash(hash)) throw new Error('Invalid ipfs hash');

    const gatewayUrls = process.env.NEXT_PUBLIC_IPFS_GATEWAY_URLS?.split(', ');

    if (!gatewayUrls) return;

    const response = await parallelFetch(
      gatewayUrls.map((url) => `${url}/ipfs/${hash}`)
    );

    return response;
  } catch (error) {
    console.error('All requests failed:', error);
    throw new Error(FAILED_IPFS_FETCH_ERROR);
  }
};

export const decryptFile = async (
  decryptionInfo: any,
  contentIpfsHash: string
): Promise<Record<string, any>> => {
  const fetchedResponse = await withRetry(
    () => fetchFromIPFS(contentIpfsHash),
    2
  );

  if (!fetchedResponse || !fetchedResponse.body)
    throw new Error(FAILED_IPFS_FETCH_ERROR);

  return penumbraDecryptFile(decryptionInfo, fetchedResponse);
};

export const decryptOwnerFile = async (
  decryptionInfo: any,
  contentIpfsHash: string
): Promise<Record<string, any>> => {
  const gatewayUrls = process.env.NEXT_PUBLIC_IPFS_GATEWAY_URLS?.split(', ');
  if (!gatewayUrls) throw new Error(FAILED_IPFS_FETCH_ERROR);

  const fetchedResponse = await sequentialFetch(
    gatewayUrls.map((url) => `${url}/ipfs/${contentIpfsHash}`)
  );

  if (!fetchedResponse || !fetchedResponse.body)
    throw new Error(FAILED_IPFS_FETCH_ERROR);
  return penumbraDecryptFile(decryptionInfo, fetchedResponse);
};

export const getIPFSAsset = async ({ ipfsHash }: { ipfsHash: string }) => {
  const fetchResponse = await withRetry(() => fetchFromIPFS(ipfsHash), 2);
  if (!fetchResponse) throw new Error(FAILED_IPFS_FETCH_ERROR);

  const data = await fetchResponse.json();
  return { data };
};

const sequentialFetch = async (urls: string[]) => {
  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (response.status === 200) {
        return response;
      }
    } catch (error) {
      console.error(`Failed to fetch from ${url}`, error);
    }
  }
};

export const getIPFSAssetSequential = async (ipfsHash: string) => {
  const gatewayURL = process.env.NEXT_PUBLIC_IPFS_GATEWAY_URLS?.split(', ');
  if (!gatewayURL || !gatewayURL.length) return;

  const response = await sequentialFetch(
    gatewayURL.map((url) => `${url}/ipfs/${ipfsHash}`)
  );

  if (!response) throw new Error(FAILED_IPFS_FETCH_ERROR);

  const data = await response.json();
  return { data };
};

export const getBaseDDoc = async (
  ddocId: string,
  portalAddress = ''
): Promise<IDdoc> => {
  const commentKey = await generateRandomAESKey();
  const rtcKey = generateDocWsKey();
  return {
    portalAddress,
    createdAt: Date.now(),
    title: 'Untitled',
    ddocId,
    content: '',
    last_updated: Date.now(),
    lastPublishedContent: null,
    linkKey: null,
    onChainFileId: null,
    publishStatus: PublishStatus.Unpublished,
    lastPublishStartTime: null,
    linkKeyNonce: null,
    isDeleted: false,
    deletedAt: null,
    commentKey,
    rtcKey,
    version: LATEST_DDOC_VERSION,
    markedForDeletion: false,
  };
};

export const resolveReceiptAndParseLog = async (
  hash: Hex,
  eventName: PortalContractEvent,
  ddocId: string,
  walletAddress: string,
  timeout: number
) => {
  const receipt = await waitForUserOpReceipt(hash, timeout);

  const parsedLog = parseEventLogs({
    abi: CONTRACT_ABI_MAP[ContractType.Portal],
    eventName,
    logs: receipt.logs,
  });

  if (parsedLog.length === 0) {
    await modifyUserCacheData(ddocId, walletAddress, {
      pendingTxnHash: null,
    });

    throw new Error(`No ${eventName} Event Found!`);
  }

  return parsedLog;
};

export const getDecrytedString = (
  encryptedString: string,
  nonce: string,
  secretKey: Uint8Array
) => {
  const decryptedValue = tweetnacl.secretbox.open(
    toUint8Array(encryptedString),
    toUint8Array(nonce),
    secretKey
  );

  if (decryptedValue) return new TextDecoder().decode(decryptedValue);

  return decryptedValue;
};

export const decryptTitle = async (
  encryptedTitle: string,
  fileKey: {
    key: string;
    iv: string;
    authTag: string;
  },
  nonce: string,
  secretKey: Uint8Array,
  archVersion: string | undefined
) => {
  if (archVersion === ARCH_VERSION)
    return decryptTitleWithFileKey(encryptedTitle, fileKey);
  return getDecrytedString(encryptedTitle, nonce, secretKey);
};

export const checkAndUpdatePendingTxn = async (
  existingCache: UserCache,
  docItem: IDdoc,
  walletAddress: string
) => {
  if (!existingCache?.pendingTxnHash) return null;

  let parsedLog = null;

  try {
    const status = await getUserOpStatus(existingCache.pendingTxnHash);

    if (FAILED_USER_OP_STATUSES.includes(status)) {
      await modifyUserCacheData(docItem.ddocId, walletAddress, {
        pendingTxnHash: null,
      });
      throw new Error(`User operation failed: ${status}`);
    }

    parsedLog = await resolveReceiptAndParseLog(
      existingCache.pendingTxnHash,
      isNullish(docItem.onChainFileId)
        ? PortalContractEvent.AddedFile
        : PortalContractEvent.EditedFile,
      docItem.ddocId,
      walletAddress,
      PENDING_TXN_TIMEOUT
    );
  } catch (error) {
    return null;
  }

  if (!parsedLog) return null;

  // @ts-ignore
  const onChainFileId = Number(parsedLog[0].args.fileId);

  await modifyUserCacheData(docItem.ddocId, walletAddress, {
    onChainFileId,
    publishStatus: PublishStatus.Published,
    pendingTxnHash: null,
  });

  const existingLocalDoc = await getExistingDocOnIndexeddb(docItem.ddocId);
  if (existingLocalDoc) {
    await db.ddocs.update(docItem.ddocId, {
      onChainFileId,
      linkKey: existingCache.linkKey,
      linkKeyNonce: existingCache.linkKeyNonce,
      publishStatus: PublishStatus.Published,
      lastPublishedContent: {
        content: docItem.content,
        title: docItem.title,
      } as any,
      lastPublishStartTime: null,
      portalAddress: KeyStoreInstance.getPortalAddress(),
    });
  }

  return { onChainFileId };
};

export const handleOnChainPublishing = async (
  walletAddress: string,
  docItem: IDdoc,
  config?: { onTxHash: (txHash: string) => void }
) => {
  const existingCacheData = await getCachedDoc(walletAddress, docItem.ddocId);

  try {
    await modifyUserCacheData(docItem.ddocId, walletAddress, {
      publishStatus: PublishStatus.InProgress,
      migrationStatus:
        existingCacheData?.archVersion === ARCH_VERSION
          ? null
          : MigrationStatus.InProgress,
    });

    if (existingCacheData) {
      // check this at the top so we dont make unnecessary calls
      // for upload to IPFS and other things
      const { onChainFileId } =
        (await checkAndUpdatePendingTxn(
          existingCacheData,
          docItem,
          walletAddress
        )) || {};

      if (!isNullish(onChainFileId)) return { onChainFileId };

      if (existingCacheData.linkKey && !docItem.linkKey)
        docItem.linkKey = existingCacheData.linkKey;
      if (existingCacheData.linkKeyNonce && !docItem.linkKeyNonce)
        docItem.linkKeyNonce = existingCacheData.linkKeyNonce;
      if (
        !isNullish(existingCacheData.onChainFileId) &&
        isNullish(docItem.onChainFileId)
      )
        docItem.onChainFileId = existingCacheData.onChainFileId;
    }

    let isPortalAddressChanged = false;

    if (!isNullish(docItem.onChainFileId)) {
      if (
        existingCacheData &&
        existingCacheData.portalAddress?.toLowerCase() !==
          KeyStoreInstance.getPortalAddress().toLowerCase()
      ) {
        docItem.onChainFileId = null; // ddoccache

        await modifyUserCacheData(docItem.ddocId, walletAddress, {
          onChainFileId: null,
        });
        isPortalAddressChanged = true;
      } else if (
        docItem.portalAddress.toLowerCase() !==
        KeyStoreInstance.getPortalAddress().toLowerCase()
      ) {
        docItem.onChainFileId = null;

        await modifyUserCacheData(docItem.ddocId, walletAddress, {
          onChainFileId: null,
        });
        isPortalAddressChanged = true;
      }
    }

    const { encryptedSecretKey, nonce, secretKey, hasLinkKeyChanged } =
      await WorkerInstance.generateEncryptionMaterial(docItem);

    const contentFile = jsonToFile(
      { file: docItem.content, source: FILE_SOURCE_MAP.DDOC },
      fromUint8Array(generateRandomNonce())
    );
    const { fileKey, encryptedFile } = await encryptFile(contentFile);

    const fileMetadata = await generateFileMetadata(
      contentFile.size,
      fileKey,
      docItem,
      secretKey,
      nonce,
      encryptedSecretKey,
      docItem.version,
      docItem.rtcKey
    );

    const { contentIpfsHash, metadataIpfsHash } =
      await uploadContentAndMetadataFile({
        contentFile: encryptedFile,
        metadataJSON: fileMetadata,
      });

    await modifyUserCacheData(docItem.ddocId, walletAddress, {
      linkKey: encryptedSecretKey,
      linkKeyNonce: fromUint8Array(nonce),
      ownerLock: fileMetadata.ownerLock,
      portalLock: fileMetadata.portalLock,
      portalAddress: KeyStoreInstance.getPortalAddress(),
    });

    const txHash = await addOrEditFileTransaction({
      contractAddress: KeyStoreInstance.getPortalAddress(),
      contentIpfsHash,
      metadataIpfsHash,
      fileType: FileTypeEnum.PRIVATE,
      fileId: docItem.onChainFileId as number,
    });

    config?.onTxHash(contentIpfsHash);

    await modifyUserCacheData(docItem.ddocId, walletAddress, {
      pendingTxnHash: txHash,
    });

    const parsedLog = await resolveReceiptAndParseLog(
      txHash,
      isNullish(docItem.onChainFileId)
        ? PortalContractEvent.AddedFile
        : PortalContractEvent.EditedFile,
      docItem.ddocId,
      walletAddress,
      FILE_TRX_TIMEOUT
    );

    // @ts-ignore
    const onChainFileId = Number(parsedLog[0].args.fileId);

    const existingLocalDoc = await getExistingDocOnIndexeddb(docItem.ddocId);
    if (existingLocalDoc) {
      await db.ddocs.update(docItem.ddocId, {
        onChainFileId,
        linkKey: encryptedSecretKey,
        linkKeyNonce: fromUint8Array(nonce),
        publishStatus: PublishStatus.Published,
        lastPublishedContent: {
          content: docItem.content,
          title: docItem.title,
        } as any,
        lastPublishStartTime: null,
        portalAddress: KeyStoreInstance.getPortalAddress(),
      });
    }

    await modifyUserCacheData(docItem.ddocId, walletAddress, {
      onChainFileId,
      publishStatus: PublishStatus.Published,
      migrationStatus:
        existingCacheData?.archVersion === ARCH_VERSION
          ? null
          : MigrationStatus.Completed,
      pendingTxnHash: null,
      archVersion: ARCH_VERSION,
      hasLinkKeyChanged: isPortalAddressChanged
        ? true
        : hasLinkKeyChanged
          ? !isNullish(docItem.onChainFileId)
          : false,
    });

    return {
      onChainFileId,
      linkKey: encryptedSecretKey,
      linkKeyNonce: fromUint8Array(nonce),
      contentIpfsHash,
    };
  } catch (err) {
    console.error(err);
    captureException(err);

    await db.ddocs.update(docItem.ddocId, {
      publishStatus: PublishStatus.Failed,
      lastPublishStartTime: null,
    });
    await modifyUserCacheData(docItem.ddocId, walletAddress, {
      publishStatus: PublishStatus.Failed,
      migrationStatus:
        existingCacheData?.archVersion === ARCH_VERSION
          ? null
          : MigrationStatus.Failed,
    });
  }
};
export const isMergeableVersion = (version: string | undefined) => {
  if (!version) return false;
  const _version = Number(version);
  return _version >= Number(VERSION_AFTER_YJS_UPDATE);
};

export const penumbraDecryptFile = async (
  decryptionInfo: any,
  response: Response
) => {
  if (!response.body) throw new Error(FAILED_IPFS_FETCH_ERROR);
  let fileSize = Number(response.headers.get('Content-Length') || 0);
  if (!fileSize) {
    const responseArrayBuffer = await response.clone().arrayBuffer();
    fileSize = responseArrayBuffer.byteLength;
  }
  const penumbraEncryptedFile = {
    id: 0,
    stream: response.body,
    size: fileSize,
  };

  await setPenumbraWorkerLocation();
  const penumbra = getPenumbra();
  const decryptedFileContent = (
    await penumbra.decrypt(decryptionInfo, penumbraEncryptedFile)
  )[0];

  const contentBlob = await penumbra.getBlob(decryptedFileContent);
  const contentText = await contentBlob.text();

  return JSON.parse(contentText);
};

export const getBaseUserCache = (
  partialCache: Partial<UserCache> = {}
): UserCache => {
  return {
    ddocId: '',
    title: '',
    last_updated: Date.now(),
    createdAt: Date.now(),
    publishStatus: PublishStatus.Unpublished,
    portalAddress: '',
    onChainFileId: null,
    linkKey: null,
    linkKeyNonce: null,
    encryptedTitle: null,
    archVersion: ARCH_VERSION,
    address: '',
    ownerLock: null,
    portalLock: null,
    needToDecryptTitle: false,
    commentKey: '',
    markedForDeletion: false,
    ...partialCache,
  };
};
